import { CheckCircle2, Clock3 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { fmtMoney } from '@/features/shared/format'
import { usePayments } from '../../api'
import type { OdemeTur } from '../../types'

const DOT: Record<string, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  info: 'bg-info',
  primary: 'bg-primary',
}

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1)

export function OdemeSideCards() {
  return (
    <div className="space-y-4">
      <OdemeTakvimCard />
      <SonIslemlerCard />
    </div>
  )
}

const TONE: Record<OdemeTur, string> = { maas: 'success', avans: 'warning', prim: 'warning', yemek: 'info', ulasim: 'info', masraf: 'info' }

function OdemeTakvimCard() {
  const { t } = useTranslation()
  const payments = usePayments().data ?? []
  const now = new Date()
  const prefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-`
  const dots = new Map<number, string>()
  for (const row of payments) {
    if (!row.tarih.startsWith(prefix)) continue
    const day = Number(row.tarih.slice(8, 10))
    if (day) dots.set(day, TONE[row.tur])
  }
  return (
    <Card className="rounded-2xl border-border/70 shadow-sm">
      <CardHeader>
        <CardTitle className="text-base font-semibold">{t('hr.personel.odemeler.takvim')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-2 grid grid-cols-7 gap-1 text-center text-[10px] font-medium uppercase text-muted-foreground">
          {['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pz'].map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {DAYS.map((d) => {
            const tone = dots.get(d)
            return (
              <div
                key={d}
                className={cn('relative flex h-8 items-center justify-center rounded-md text-[11px] tabular-nums', d === 15 && 'bg-primary/10 font-semibold text-primary')}
              >
                {d}
                {tone ? <span className={cn('absolute right-1 bottom-1 size-1.5 rounded-full', DOT[tone])} /> : null}
              </div>
            )
          })}
        </div>
        <div className="mt-3 flex flex-wrap gap-2 text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1"><span className="size-1.5 rounded-full bg-success" />{t('hr.personel.odeme.tur.maas')}</span>
          <span className="inline-flex items-center gap-1"><span className="size-1.5 rounded-full bg-warning" />{t('hr.personel.odeme.tur.avans')}</span>
          <span className="inline-flex items-center gap-1"><span className="size-1.5 rounded-full bg-info" />{t('hr.personel.odeme.tur.masraf')}</span>
        </div>
      </CardContent>
    </Card>
  )
}

function SonIslemlerCard() {
  const { t } = useTranslation()
  const islemler = (usePayments().data ?? []).slice(0, 8).map((row) => ({
    id: row.id,
    title: t(`hr.personel.odeme.tur.${row.tur}`),
    detail: `${row.ad} · ${fmtMoney(row.tutar)}`,
    when: row.tarih,
    tone: row.durum === 'odendi' ? 'success' : row.durum === 'onay_bekliyor' ? 'warning' : 'info',
  }))
  return (
    <Card className="rounded-2xl border-border/70 shadow-sm">
      <CardHeader>
        <CardTitle className="text-base font-semibold">{t('hr.personel.odemeler.sonIslemler')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {islemler.map((s) => (
          <div key={s.id} className="flex gap-3">
            <div
              className={cn(
                'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full',
                s.tone === 'success' && 'bg-success/12 text-success',
                s.tone === 'warning' && 'bg-warning/15 text-warning',
                s.tone === 'info' && 'bg-info/12 text-info',
              )}
            >
              {s.tone === 'warning' ? <Clock3 className="size-3.5" /> : <CheckCircle2 className="size-3.5" />}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium">{s.title}</div>
              <div className="truncate text-xs text-muted-foreground">{s.detail}</div>
              <div className="text-[11px] text-muted-foreground">{s.when}</div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
